import { useEffect, useState } from "react"
import { Check, MinusCircle, XCircle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

// Walks each unfinalized rock one at a time, prompting the member to
// mark it met / partially met / missed. Each choice fires
// onFinalize(rockId, result). Closes when all rocks are handled or the
// member dismisses.
//
// Two modes share this UX:
//   - mode="wrap_up" (default): end-of-quarter wrap-up trigger from the
//     dashboard banner. Closes the modal when done; nothing else happens.
//   - mode="rollover": precondition for "Start a new quarter". After the
//     last rock is finalized, fires onComplete() so the parent can POST
//     the rollover request. Copy is adjusted to set expectations.
//
// Props:
//   open: bool
//   onOpenChange(next): controls open state
//   rocks: array (only unfinalized rocks are walked)
//   onFinalize(rockId, result): called per choice; parent owns the PATCH
//   mode: "wrap_up" | "rollover"
//   onComplete: optional callback fired after the last rock is finalized
const RESULT_OPTIONS = [
  {
    value: "met",
    label: "Met",
    description: "You finished this rock this quarter.",
    icon: Check,
    className: "bg-green-600 hover:bg-green-700 text-white",
  },
  {
    value: "partially_met",
    label: "Partially met",
    description: "You made meaningful progress but didn't fully land it.",
    icon: MinusCircle,
    className: "bg-amber-500 hover:bg-amber-600 text-white",
  },
  {
    value: "missed",
    label: "Not this quarter",
    description: "This one will carry forward or be dropped.",
    icon: XCircle,
    className: "bg-zinc-600 hover:bg-zinc-700 text-white",
  },
]

export function RockFinalizeModal({
  open,
  onOpenChange,
  rocks = [],
  onFinalize,
  mode = "wrap_up",
  onComplete,
  hookError,
}) {
  // Snapshot the active set when the modal opens so subsequent optimistic
  // updates don't renumber the walk-through mid-flight ("2 of 3" drifting
  // to "1 of 2" as rock 1's result lands). Snapshot is recomputed on each
  // open; it also lets the counter stay accurate even when onFinalize's
  // PATCH is still in flight.
  const [walkthrough, setWalkthrough] = useState([])
  const [handledIds, setHandledIds] = useState([])
  const [isWorking, setIsWorking] = useState(false)
  const [localError, setLocalError] = useState(null)

  useEffect(() => {
    if (open) {
      setWalkthrough(rocks.filter((r) => !r.result))
      setHandledIds([])
      setIsWorking(false)
      setLocalError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const remaining = walkthrough.filter((r) => !handledIds.includes(String(r.id)))
  const current = remaining[0]
  const done = !current

  const handleChoice = async (result) => {
    if (!current || isWorking) return
    setLocalError(null)
    setIsWorking(true)
    try {
      const ok = await onFinalize(current.id, result)
      if (ok !== false) {
        setHandledIds((prev) => [...prev, String(current.id)])
      }
    } catch (e) {
      setLocalError(e?.message || "Couldn't save. Try again.")
    } finally {
      setIsWorking(false)
    }
  }

  const handleOpenChange = (next) => {
    onOpenChange(next)
    if (!next) {
      setHandledIds([])
      setWalkthrough([])
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {done
              ? mode === "rollover"
                ? "Ready for your next cycle"
                : "That's a wrap on this quarter"
              : "Wrap up this rock"}
          </DialogTitle>
          <DialogDescription>
            {done
              ? mode === "rollover"
                ? "All current rocks are finalized. Continue to pick when your next 13-week cycle starts and add your first rock."
                : "Your finalized rocks move to your history. Your next quarter starts with a clean set."
              : `${walkthrough.length - remaining.length + 1} of ${walkthrough.length}`}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center space-y-2">
            {mode === "rollover" ? (
              <Button
                type="button"
                variant="gradient"
                onClick={() => {
                  onComplete?.()
                  handleOpenChange(false)
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                type="button"
                variant="gradient"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md bg-zinc-50 border border-zinc-200 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                Rock
              </p>
              <p className="font-display text-lg text-zinc-900">{current.title}</p>
              <p className="text-xs text-zinc-500 mt-1">
                Final progress: {current.progressPct || 0}%
              </p>
            </div>

            <div className="space-y-2">
              {(hookError || localError) && (
                <div
                  role="alert"
                  className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
                >
                  {localError || hookError}
                </div>
              )}
              {RESULT_OPTIONS.map((opt) => {
                const Icon = opt.icon
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChoice(opt.value)}
                    disabled={isWorking}
                    className={`w-full text-left rounded-md px-4 py-3 flex items-start gap-3 transition-colors disabled:opacity-50 ${opt.className}`}
                  >
                    <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                    <span>
                      <span className="block font-semibold">{opt.label}</span>
                      <span className="block text-xs opacity-90 mt-0.5">
                        {opt.description}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
